import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getAuthenticatedUser, login as loginRequest, logout as logoutRequest, refreshSession } from '../services/apis';
import { FLUJOS_APPLICATION_CODE, hasApplicationAccess } from '../utils/applicationAccess';

const AuthContext = createContext(null);
const missingFlujosAccessMessage = 'El usuario no tiene acceso a la aplicacion Flujos.';

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFlujosAccess = useMemo(() => hasApplicationAccess(authData), [authData]);

  const checkSession = useCallback(async () => {
    setLoading(true);

    try {
      const userData = await getAuthenticatedUser();
      setAuthData(userData);
      return userData;
    } catch (error) {
      try {
        await refreshSession();
        const refreshedUserData = await getAuthenticatedUser();
        setAuthData(refreshedUserData);
        return refreshedUserData;
      } catch {
        setAuthData(null);
        return null;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async ({ username, password }) => {
    const userData = await loginRequest({
      login: username,
      password,
    });

    if (!hasApplicationAccess(userData)) {
      try {
        await logoutRequest();
      } finally {
        setAuthData(null);
      }

      throw new Error(missingFlujosAccessMessage);
    }

    setAuthData(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      setAuthData(null);
    }
  }, []);

  const value = useMemo(() => ({
    authData,
    user: authData?.user || null,
    applications: authData?.applications || [],
    session: authData?.session || null,
    applicationCode: FLUJOS_APPLICATION_CODE,
    hasFlujosAccess,
    isAuthenticated: Boolean(authData?.user) && hasFlujosAccess,
    loading,
    login,
    logout,
    checkSession,
  }), [authData, checkSession, hasFlujosAccess, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }

  return context;
};
